import { readScopeCompany, writeScopeCompany } from './scopeCompany';
import { withConfidentialUnlock, noteConfidentialResponse } from './confidentialUnlock';

// API 베이스 URL — 운영은 **빈 문자열(상대경로)** 이다.
//   같은 번들을 여러 도메인이 공유하려면(pinegroup.cloud / meetinglink.kr) 절대 URL 이 박히면 안 된다.
//   절대 URL 이 박히면 다른 도메인에서 열어도 API 가 pinegroup 으로 가고,
//   refresh 쿠키(SameSite=Lax)가 cross-site 로 전송되지 않아 15분 뒤 로그아웃된다.
//   ※ `||` 가 아니라 `??` 여야 한다 — 빈 문자열은 falsy 라 `||` 로는 fallback 으로 떨어진다.
//   ※ 기본값도 '' (상대경로). 키가 사라져도 상대경로로 안전하게 동작한다.
//   개발은 web/.env.development 의 http://localhost:5000 을 그대로 쓴다.
const BASE_URL = process.env.REACT_APP_API_BASE_URL ?? '';
const AUTH_EXCLUDE_PATHS = ['/api/login', '/api/signup', '/api/auth/refresh'];

// 위장 접속 토큰 저장 키 (sessionStorage — 탭 단위로 격리)
const IMP_TOKEN_KEY = 'pg_imp_token';

let refreshPromise = null;

/** 위장 접속 중인지 확인 */
function isImpersonating() {
  return Boolean(sessionStorage.getItem(IMP_TOKEN_KEY));
}

/** 위장 접속 토큰 초기화 (새 탭 진입 시 URL 파라미터에서 저장) */
function initImpersonation(token) {
  if (!token) return;
  sessionStorage.setItem(IMP_TOKEN_KEY, token);
}

/** 위장 접속 종료 (sessionStorage 제거 후 로그인 페이지로 이동) */
function exitImpersonation() {
  sessionStorage.removeItem(IMP_TOKEN_KEY);
  window.location.replace('/login');
}

/**
 * 현재 요청에 사용할 토큰 반환
 * - 위장 접속 중: sessionStorage의 imp_token 우선 사용
 * - 일반 접속: localStorage의 accessToken 사용
 */
function getToken() {
  const impToken = sessionStorage.getItem(IMP_TOKEN_KEY);
  if (impToken) return impToken;
  return localStorage.getItem('accessToken');
}

function setToken(token) {
  if (!token) return;
  localStorage.setItem('accessToken', token);
}

function clearTokenAndRedirect() {
  if (isImpersonating()) {
    // 위장 접속 중 토큰 만료 → 탭을 닫거나 로그인으로 이동
    exitImpersonation();
    return;
  }
  localStorage.removeItem('accessToken');
  if (window.location.pathname !== '/login') {
    window.location.replace('/login');
  }
}

function withAuth(headers = {}) {
  const token = getToken();
  if (!token) return headers;
  return withScopeCompany({ ...headers, Authorization: `Bearer ${token}` });
}

// 운영자가 고른 고객 테넌트 회사를 싣는다 (2026-09-15, scopeCompany.js 참고).
//   위장 접속 중에는 싣지 않는다 — 그때 요청 주체는 운영자가 아니다.
function withScopeCompany(headers) {
  if (isImpersonating()) return headers;
  const code = readScopeCompany();
  return code ? { ...headers, 'X-Scope-Company': code } : headers;
}

function shouldTryRefresh(path, status) {
  if (status !== 401) return false;
  if (!getToken()) return false;
  if (AUTH_EXCLUDE_PATHS.includes(path)) return false;
  if (isImpersonating()) return false; // 위장 접속 토큰은 갱신 불가 (단기 전용)
  return true;
}

async function toResult(response) {
  const contentType = response.headers.get('content-type') || '';
  const data = contentType.includes('application/json') ? await response.json() : await response.text();
  return {
    ok: response.ok,
    status: response.status,
    data,
  };
}

async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;

  // 동시 401 요청이 많아도 refresh는 1번만 수행합니다.
  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      const result = await toResult(response);

      if (!result.ok) return false;
      const nextAccessToken = result.data?.tokens?.accessToken;
      if (!nextAccessToken) return false;

      setToken(nextAccessToken);
      return true;
    } catch (_error) {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

// ── as_user_id drill-down 자동 부착 (admin "내 정보" 직원 선택, 2026-04-29) ──
// URL 에 ?as_user_id=N 있으면 페이지 데이터 fetch 에 부착 → 백엔드 authenticateToken 가 user 변환.
// ⚠️ 사이드바/메타 endpoints 는 admin 본인 데이터 유지를 위해 제외 (DRILL_DOWN_EXCLUDE).
const DRILL_DOWN_EXCLUDE = [
  '/api/me',                          // 사이드바 사용자 정보
  '/api/my/permissions',              // useAppPermissions 권한
  '/api/my/work-feature-assignments', // useAppPermissions tasks
  '/api/my/external-companies',       // 사이드바 외부회사 아코디언
  '/api/my/integrated-menus',         // 사이드바 통합 메뉴
  '/api/my/menu-categories',          // 사이드바 분류 (보는 사람 본인 권한 기준)
  '/api/my/tenant-menu-info',         // 일일콜 관리 메뉴 이름 (보는 사람 자기 테넌트, 2026-09-24)
  '/api/my/preferences/',             // 모바일 바로가기 (admin 본인 설정)
  '/api/admin/',                      // admin 페이지 자체
  '/api/auth/', '/api/login', '/api/logout', '/api/signup', // 인증
];
function shouldAppendDrillDown(path) {
  return !DRILL_DOWN_EXCLUDE.some((pre) => path.startsWith(pre));
}
function appendDrillDownQuery(path) {
  if (typeof window === 'undefined') return path;
  if (!shouldAppendDrillDown(path)) return path;
  if (path.includes('as_user_id=')) return path; // 명시 우선
  const params = new URLSearchParams(window.location.search);
  const asUserId = params.get('as_user_id');
  if (!asUserId) return path;
  const sep = path.includes('?') ? '&' : '?';
  return `${path}${sep}as_user_id=${encodeURIComponent(asUserId)}`;
}

async function fetchJson(path, options = {}) {
  const finalPath = appendDrillDownQuery(path);
  const response = await fetch(`${BASE_URL}${finalPath}`, {
    ...options,
    headers: withConfidentialUnlock(path, options.headers), // 대외비 주소에만 출입증(confidentialUnlock.js, 2026-10-16)
    credentials: 'include',
  });
  noteConfidentialResponse(path, response);
  return toResult(response);
}

async function requestWithRetry(path, options = {}, hasRetried = false) {
  const result = await fetchJson(path, options);

  if (!hasRetried && shouldTryRefresh(path, result.status)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const retryHeaders = withAuth(options.headers || {});
      return requestWithRetry(path, { ...options, headers: retryHeaders }, true);
    }
    clearTokenAndRedirect();
  }

  return result;
}

async function request(method, path, body, options = {}) {
  const headers = withAuth({
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  });

  return requestWithRetry(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

async function get(path, options = {}) {
  return requestWithRetry(path, {
    method: 'GET',
    headers: withAuth(options.headers || {}),
  });
}

async function post(path, body, options = {}) {
  return request('POST', path, body, options);
}

/**
 * FormData(파일 업로드)용 POST — JSON.stringify를 건너뛰고 Content-Type도 브라우저에 맡김
 * @param {string} path
 * @param {FormData} formData
 */
async function postForm(path, formData) {
  // Content-Type을 명시하지 않아야 브라우저가 boundary 포함 multipart/form-data를 자동 설정
  const headers = withAuth({});
  return requestWithRetry(path, {
    method: 'POST',
    headers,
    body: formData,
  });
}

async function put(path, body, options = {}) {
  return request('PUT', path, body, options);
}

async function patch(path, body, options = {}) {
  return request('PATCH', path, body, options);
}

async function del(path, options = {}) {
  return requestWithRetry(path, {
    method: 'DELETE',
    headers: withAuth(options.headers || {}),
  });
}

async function download(path) {
  const response = await fetch(`${BASE_URL}${path}`, {
    method: 'GET',
    headers: withAuth({}),
    credentials: 'include',
  });

  if (shouldTryRefresh(path, response.status)) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      const retryResponse = await fetch(`${BASE_URL}${path}`, {
        method: 'GET',
        headers: withAuth({}),
        credentials: 'include',
      });
      return {
        ok: retryResponse.ok,
        status: retryResponse.status,
        blob: await retryResponse.blob(),
      };
    }
    clearTokenAndRedirect();
  }

  return {
    ok: response.ok,
    status: response.status,
    blob: await response.blob(),
  };
}

async function logout() {
  try {
    await fetch(`${BASE_URL}/api/logout`, {
      method: 'POST',
      headers: withAuth({ 'Content-Type': 'application/json' }),
      credentials: 'include',
    });
  } finally {
    localStorage.removeItem('accessToken');
    writeScopeCompany(null); // 다음에 로그인하는 사람에게 남기지 않는다
  }
}

export const apiClient = {
  get,
  post,
  postForm,
  put,
  patch,
  del,
  delete: del,
  download,
  logout,
  isImpersonating,
  initImpersonation,
  exitImpersonation,
};
